// WHICH TEXTURE A KUBE WEARS — the name→appearance decision, and nothing else.
//
// Split out of TextureEngine.js for one reason: that module imports THREE at module scope from an absolute path, so
// nothing in it can be imported by a node test, and "no kube renders as a flat colour" is exactly the kind of promise
// that decays silently the next time someone adds a kube to the catalogue. This file has NO imports at all — same
// shape as bit-slot.js / place-cell.js / ore-veins.js / fixture-scatter.js, all of which were split out for the same
// reason — so tests/kube-textures.test.mjs can walk the real kubes.json + products.json and assert the answer.
//
// TextureEngine.js re-exports everything here, so every existing caller is untouched.
//
// The see-through rule (isGlassKubeName) is deliberately NOT here even though it is the same kind of question: its
// answer is derived from the CATALOGUES (a product whose source regolith exists), and this module knows only strings.
// It lives in system-engine.js and the call sites import it from there.

// Crafted products are named goods, not ores, so they are routed by NAME and nothing else. Routing them on metalness
// (as the ore branch does) cannot work: a recipe's output is as metallic as the recipe made it — Heavy Water is 0.0,
// Liquid Argon 0.0, Silicon Carbide 0.2, Graphene 0.35, Superconductor 0.55, Hyper-Cor Matrix 0.6 — so six of the
// nine crafted looks sat below any metal gate and rendered flat.
//
// ORDER MATTERS AND IS LOAD-BEARING: the loop returns the FIRST needle that matches as a substring, so a shorter
// needle placed above a longer one that contains it will shadow it forever. Every pair where one needle is a
// substring of another must have the LONGER one first. Known pairs today:
//     'crafting bench ii'  before  'crafting bench i'
// and the reason no ore name collides is checked by tests/kube-textures.test.mjs rather than asserted here.
export const CRAFTED_TEXTURES = [
    ['silicon carbide', 'siliconcarbide'],
    ['graphene',        'graphene'],
    ['brass',           'brass'],
    ['inconel',         'inconel'],
    ['sulfuric acid',   'sulfuricacid'],
    ['heavy water',     'heavywater'],
    ['liquid argon',    'liquidargon'],
    ['superconductor',  'superconductor'],
    ['hyper-cor',       'hypercor'],
    ['exotic matter',   'exoticmatter'],   // a black hole's debris (the owner's picks, Oct 27, B8): the capstone's glowing lattice, in its own violet
    // ALIEN ANIMALS (the owner's picks, Oct 28): what animals give, each a slot of its own. A needle that
    // starts with '=' is the WHOLE name, not a part of one: 'bone' is in every Herringbone biolith and 'hide' in the Hide
    // Strap module, which are not these.
    ['shell plate',     'fshell'],
    ['glow gland',      'fgland'],
    ['venom sac',       'fvenom'],
    ['=hide',           'fhide'],
    ['=bone',           'fbone'],
    ['=fibre',          'ffibre'],
    ['=eggs',           'feggs'],
    ['=scales',         'fscales'],
    ['=feathers',       'ffeather'],   // (the owner's Alien Animals items, Oct 29: shed by flyers)
    ['=crystal shard',  'fcrystal'],   // (seeded animals, Oct 30: silicon life's)
    ['=tholin gel',     'ftholin'],    // (…and methane life's)
    // The CRAFT SURFACES — the two benches and the four machines. All six wear createCraftSurfaceTexture, each from
    // its own cache slot (same shape as siliconcarbide/graphene: one generator, separate caches) so each bakes from
    // its own hex rather than wearing a sibling's tinted by material.color.
    ['toolbench',            'benchi'],   // also every 'Toolbench (<material>)'
    ['workbench',            'workbench'],   // the furniture (Oct 19) — BEFORE 'bench', which is a substring of it
    ['induction smelter',    'smelter'],
    ['electrolyzer',         'electrolyzer'],
    ['chemical synthesizer', 'chemsynth'],
    ['quantum assembler',    'quantumasm'],
    // THE STORAGE MACHINES (Oct 11). In the world they wear their icon's faces (machine-icons.mjs), as every machine with
    // a lattice design does, so this is the texture of last resort (a page that draws a plain kube). A slot of their own,
    // so a crate of Iron does not fall through to the iron lattice and share Iron's look; NOT a craft surface (no panel to
    // open, and isCraftSurfaceName stays the benches and stations).
    ['cargo crate side',     'crateside'],    // LONGER NEEDLE FIRST: the two-kube crate's right half (Oct 21)
    ['cargo crate',          'cargocrate'],   // also every 'Cargo Crate (<material>)'
    ['vending machine',      'vending'],
    // THE DOCKING PORT (Oct 12): the part a station is launched from, and its core block. Plated metal, a slot of its own.
    ['docking port',         'dockport'],
    // VESSELS, PHASE 2 (Oct 12): the Suit Dock. In the world it wears its icon's faces (machine-icons.mjs); these slots are
    // the texture of last resort, each its own so none shares a metal's look, its upper half included.
    ['suit dock upper',      'suitdockup'],    // LONGER NEEDLE FIRST: 'suit dock' is a substring of it
    ['suit dock',            'suitdock'],
    // VESSELS, PHASE 3 (Oct 12): the two drives, each its own slot.
    ['em drive',             'emdrive'],
    ['methalox thruster',    'methalox'],
    // THE FURNITURE (Oct 19; storage-machines.mjs FURNITURE). In the world each wears its icon's faces (machine-icons.mjs);
    // these are its texture of last resort, a slot each so none shares a wood's or a metal's look. 'workbench' sits above
    // with the benches, ahead of 'bench' (a substring of it and of 'toolbench'); 'packing crate' and 'cargo crate' share
    // only 'crate', which is no needle. None is a craft surface here: the Workbench crafts through synthesis.mjs BENCH_ALSO.
    ['bed',                  'bed'],
    ['table',                'table'],
    ['bench',                'bench'],
    ['shelf',                'shelf'],
    ['cabinet',              'cabinet'],   // the Chest until the owner, Oct 21: cabinets instead of chests
    ['barrel',               'barrel'],
    ['counter',              'counter'],
    ['locker upper',         'lockerup'],     // LONGER NEEDLE FIRST (Oct 21: the Wardrobe became the Locker, two kubes high)
    ['locker',               'locker'],
    ['display case',         'display'],      // the Display Case (Oct 21)
    ['planter',              'planter'],
    ['hearth',               'hearth'],
    ['lamp post',            'lamppost'],
    ['trough',               'trough'],
    ['hide rug',             'hiderug'],      // what animals give (Oct 29): a slab of hide on the floor
    ['bone chandelier',      'chandelier'],   // …and a light of bone that hangs from a ceiling
    // THE DOORS (doors.mjs, Oct 20). In the world a door's cells are hitboxes and its panels are drawn in their material
    // (system-view.js doorLayerBuild); these are the texture of last resort for a page that draws one as a plain kube.
    // LONGER NEEDLES FIRST: 'door' is a substring of the other three.
    ['door upper',           'doorupper'],
    ['glass door',           'glassdoor'],
    ['double door',          'doubledoor'],
    ['door',                 'door'],
    ['hatch',                'hatch'],
    ['shutter',              'shutter'],
    ['rungs',                'rungs']
];

export const CRAFT_SURFACE_KEYS = new Set(['benchi', 'smelter', 'electrolyzer', 'chemsynth', 'quantumasm']);
export function isCraftSurfaceKey(key) { return CRAFT_SURFACE_KEYS.has(key); }

// ---- A GAUGE IS NOT A SURFACE ------------------------------------------------------------------------------------
// Almost every texture here is a repeating MATERIAL — a stipple, a lattice, a weave, a frost — and the icon renderer
// deliberately zooms into one quarter of it (the halved UVs on _hbGeo) so the pattern still reads as itself at 34px
// instead of turning to mush. That is the right call for a material, where any quarter is as good as any other.
//
// It is exactly wrong for an INSTRUMENT. A battery's face is one centred dial whose entire meaning is the shape of
// the figure on it, so cropping to a corner shows a quarter of a ring — which is what a full battery looked like
// when this shipped: a gauge apparently stuck at 25% however charged it was.
//
// Membership is by texture KEY rather than by name, so a bit or a variant lands in the same answer as its base.
export const GAUGE_KEYS = new Set(['battery']);
export function isGaugeKey(key) { return GAUGE_KEYS.has(key); }

// Does this kube wear a control panel — i.e. can a placed cell of it be OPENED? Derived from the needle table, so
// the thing that draws the panel and the thing that decides a cell is interactive can never disagree.
export function isCraftSurfaceName(name) { return isCraftSurfaceKey(craftedTextureKey(name)); }

export function craftedTextureKey(name) {                        // crafted cache slot for this kube, or null if it is not a crafted kube
    const n = (name || '').toLowerCase();
    for (const [needle, key] of CRAFTED_TEXTURES) if (needle[0] === '=' ? n === needle.slice(1) : n.includes(needle)) return key;
    return null;
}

// A BIT IS AN EIGHTH OF A KUBE AND MUST LOOK LIKE ONE. Bits are catalogue entries with a `bitOf` field and — this is
// the whole bug — no `threejs-properties` of their own, so kubeTextureType saw the 0.1 metalness default and "Iron
// Bit" missed the metal gate that "Iron" clears. "Arid Mineral Bit" missed too, for a different reason: it ends in
// "Bit", not "Mineral". 79 of the 142 bits rendered as flat colour while the kube they came from was textured.
//
// The rule is a NAME rule, which is why it can live here rather than in the catalogue: tests/kube-textures.test.mjs
// checks it against the real `bitOf` data and finds the correspondence exact — every one of the 142 bits is named
// `<base> + " Bit"`, no non-bit ends in " Bit", and every stripped name is a real entry. If a future bit is ever named
// some other way that test fails rather than the texture silently vanishing.
//
// This fixes the NAME-derived branches on its own (Mineral / Regolith / crafted). The metalness gate cannot be fixed
// here — the bit genuinely has no metalness to read — so a caller that wants "Iron Bit" to reach the iron lattice has
// to pass the BASE kube's metalness. baseKubeMetalness() below is that lookup, in one place, for all three call sites.
const BIT_SUFFIX = ' Bit';
export function bitBaseName(name) {
    const nm = name || '';
    // A turf bit ("Verge Turf Bit", world version 9) is not an eighth of a kube: there is no "Verge Turf" to strip to.
    if (nm.endsWith(' Turf Bit')) return null;
    return nm.endsWith(BIT_SUFFIX) ? nm.slice(0, -BIT_SUFFIX.length) : null;
}
// The metalness kubeTextureType should be given for `name`, resolved through a catalogue lookup the caller supplies.
// `lookup` is any (name) => entry-or-null — system-view.js has kubeByName, app.js has its own. Kept here so the three
// render paths cannot drift, and so a new call site gets it right by using it rather than by remembering the rule.
export function metalnessFor(name, lookup) {
    const direct = lookup && lookup(name);
    const own = direct && direct['threejs-properties'] && direct['threejs-properties'].metalness;
    if (typeof own === 'number') return own;
    const base = bitBaseName(name);
    const baseEntry = base && lookup && lookup(base);
    const inherited = baseEntry && baseEntry['threejs-properties'] && baseEntry['threejs-properties'].metalness;
    return typeof inherited === 'number' ? inherited : 0.1;
}

// AN ICON HAS NOTHING TO REFLECT. A standard material lights a metal almost entirely by what it reflects, and a
// preview's scene is one ambient light and two lamps with nothing around them, so at the catalogue's own 0.85 to 1.0
// a metal kept a tenth of its colour and Copper, Silver, Chromium and Titanium drew nearly black. The world never had
// this problem, because its voxel shader ignores metalness. Every preview (the hotbar and codex icons in app.js,
// doc/catalogue.html, doc/kube-textures.html) takes its metalness from here: capped, a metal keeps a little sheen and
// about 85% of its colour's brightness. 0.25 is what a preview has always used for an item that gives none.
export const ICON_METALNESS_MAX = 0.35;
export function iconMetalness(m) {
    return Math.min(ICON_METALNESS_MAX, Number.isFinite(m) ? m : 0.25);
}

// ── THE MATERIAL TABLE ───────────────────────────────────────────────────────────────────────────────────────────
// EXACT NAMES, not substrings. The crafted needle table above can use substrings because its names are long and
// distinctive; these cannot. "Water" is a substring of "Water Ice", "Water Vapor" and "Heavy Water"; "Carbon" of
// "Hydrocarbon Ice" and "Silicon Carbide"; "Silicon" of "Silicon Carbide"; "Glass" of "Reinforced Glass". A needle
// table here would silently re-skin half the ice family, so membership is by exact name and the test checks that the
// table and the catalogue agree.
//
// The value is the CACHE SLOT the texture is baked into. A slot shared by many kubes needs a generator that is
// neutral and seed-stable (see texture-materials.js); a slot per kube may bake its own hex.

// The 32 melt panes all share ONE slot: createFloatGlassTexture is built on white, takes a FIXED seed rather than
// hexSeed, and never reads baseHex, so one canvas serves every tint and the wall of a glass build reads as one slab.
// Derived from the same rule isGlassKubeName uses (a "<X> Glass" whose "<X> Regolith" is a real kube) — stated here
// as names because this module is catalogue-free, and checked against the catalogue by tests/kube-textures.test.mjs.
const GLASS_PANES = new Set([
    'Arid Glass', 'Aura Glass', 'Bloom Glass',
    'Cinder Glass', 'Corona Glass', 'Drift Glass',
    'Echo Glass', 'Ether Glass', 'Farout Glass',
    'Fathom Glass', 'Flash Glass', 'Gleam Glass',
    'Glint Glass', 'Gloam Glass', 'Halo Glass',
    'Haze Glass', 'Ignition Glass', 'Lull Glass',
    'Lumina Glass', 'Nova Glass', 'Radiance Glass',
    'Reach Glass', 'Scorched Glass', 'Solis Glass',
    'Spirit Glass', 'Starlight Glass', 'Surge Glass',
    'Tide Glass', 'Veil Glass', 'Verge Glass',
    'Void Glass', 'Wisp Glass'
]);

// Reinforced Glass is NOT of that family — it is the structural product, opaque and shadow-casting — so it gets the
// laminated wire-mesh look and its own slot.
const NATIVE_TEXTURES = new Map([
    ['Reinforced Glass',  'wiredglass'],
    // Native elements, ores and rock a player mines out of the ground.
    ['Silicon',           'silicon'],       // conchoidal fracture on a polysilicon ingot
    ['Carbon',            'carbon'],        // layered graphite cleavage
    ['Potassium',         'alkali'],        // soft alkali metal under a creeping tarnish
    ['Halite',            'halite'],        // rock salt: cubic cleavage and hopper faces
    ['Bauxite',           'bauxite'],       // pisolitic — packed pea-sized ooids in a clay matrix
    ['Titanium Ore',      'titaniumore'],   // ilmenite grains disseminated through a duller host
    ['Slag',              'slag'],          // vitrified smelter waste, vesicular
    ['Aerogel',           'aerogel'],       // frozen smoke: translucent nanofoam
    ['Cor',               'cor'],           // the fictional core material
    // Frozen volatiles — comet nuclei are voxel-rendered, so these are seen at arm's length while mining.
    ['Water Ice',         'waterice'],
    ['CO2 Ice',           'co2ice'],
    ['Methane Ice',       'methaneice'],
    ['Ammonia Ice',       'ammoniaice'],
    ['Nitrogen Ice',      'nitrogenice'],
    ['Hydrocarbon Ice',   'tholin'],        // tarry organic ice, not a clear ice at all
    // Manufactured goods.
    ['Circuit Board',     'circuit'],
    ['Photovoltaic Cell', 'photovoltaic'],
    ['Power Core',        'powercore'],
    // The two energy goods wear the looks they ARE — a deployed array is photovoltaic, a battery is a power cell
    // — but from their OWN cache slots, so each bakes from its own hex instead of arriving tinted as a sibling.
    // Same shape as the craft surfaces above, and for the same reason.
    ['Solar Panel',       'solarpanel'],
    ['Battery',           'battery'],
    ['Titan Plate',       'armorplate'],
    ['Silica',            'silica'],        // the first material a new player ever gathers
    // Cryogenic liquids each bake their own hex, so each takes its own slot.
    ['Liquid Oxygen',     'cryolox'],
    ['Liquid Nitrogen',   'cryolin'],
    ['Liquid Methane',    'cryolch4'],
    ['Liquid Chlorine',   'cryolcl'],
    ['Water',             'waterflow'],     // the shipped fluid-flow look, which Sulfuric Acid and Heavy Water wear
    ['Liquid CO2',        'cryolco2'],      // only reachable above CO2's 5.18 bar triple point — rare, but the phase model can produce it
    ['Liquid Ammonia',    'cryolnh3'],
]);

// Every gas shares one slot — a pressure-vessel read over a volumetric swirl, neutral so each gas's own hex tints it.
const GAS_KUBE_NAMES = new Set([
    'Ammonia Gas', 'Argon Gas', 'Carbon Dioxide', 'Chlorine',
    'Helium', 'Hydrogen', 'Methane', 'Neon',
    'Nitrogen', 'Oxygen', 'Water Vapor', 'Xenon Gas',
]);

// The cache slot a kube's own material wants, or null. Checked AFTER the crafted needles so a crafted name always
// wins, and BEFORE the Mineral/Regolith/metal branches so nothing here can be stolen by a suffix.
export function nativeTextureKey(name) {
    const nm = name || '';
    if (GLASS_PANES.has(nm)) return 'glass';
    if (GAS_KUBE_NAMES.has(nm)) return 'gasvessel';
    return NATIVE_TEXTURES.get(nm) || null;
}
export { GLASS_PANES, GAS_KUBE_NAMES, NATIVE_TEXTURES };

// The whole dispatch, in ONE place; returns the `type` to hand applyMaterial, or null for a kube that wears no crust
// texture at all (caller leaves the material flat-coloured). The hotbar-icon and voxel-crust call sites each used to
// carry their own copy of this chain, and that duplication is exactly how the crafted routes broke: both copies only
// passed the kube NAME on the metal branch, so applyMaterial's name-keyed crafted rules were unreachable for anything
// under 0.7 metalness.
// LIFE'S MATERIALS (the owner's picks, Sep 30) wear their family's texture. Which family a name is of is organics.mjs's
// answer, and this file imports nothing, so TextureEngine.js hands the lookup in (setLifeFamilyLookup). Without it (a
// node test) life stays what it was: flat, in its own colour.
let lifeFamilyOf = null;
export function setLifeFamilyLookup(fn) { lifeFamilyOf = fn; }
// TIMBER (the owner's Timber Looks, Oct 21: 'glulam'): "<Wood pigment> Timber" and its bit wear a look of their own,
// glued laminate (texture-looks.mjs 'k-timber', picked in texture-picks.mjs), from ONE slot shared by all 23 and tinted
// by each one's own honey colour, as the crust is. Until then, as Debarked Wood (Oct 1), it borrowed its wood's bark.
// Matched by its shape, so a Timber added with a new wood wears it too; no other catalogue name ends in " Timber"
// (tests/timber.test.mjs holds that).
export const TIMBER_SLOT = 'timber';
export function timberTextureKey(name) {
    const nm = bitBaseName(name || '') || name || '';
    return /^\S+ Timber$/.test(nm) ? TIMBER_SLOT : null;
}

// THE BIOLITH KUBES (biolith.mjs, the owner's proposal confirmed Oct 13): "<Pigment> Biolith <Pattern noun>", and its
// bit. ONE canvas per PATTERN (texture-biolith.js), shared by all of its pigments and their bits, tinted by each kube's
// own hex like the crust: slot 'biolith:<pattern id>'. Matched before every other rule, so no needle elsewhere ("tile",
// a metal's name) can claim a biolith's name. The nouns are biolith.mjs's (tests/biolith.test.mjs holds the two equal);
// they are written out here because this module imports nothing.
export const BIOLITH_TEXTURES = Object.freeze({
    'Bricks': 'running', 'Stacked Bricks': 'stack', 'Herringbone': 'herring', 'Basketweave': 'basket', 'Flagstone': 'flag',
    'Tiles': 'tile', 'Polished': 'smooth', 'Fluted': 'fluted', 'Carved Kube': 'glyph',
});
export function biolithTextureKey(name) {
    const m = /^\S+ Biolith (.+)$/.exec(bitBaseName(name || '') || name || '');
    return m && Object.prototype.hasOwnProperty.call(BIOLITH_TEXTURES, m[1]) ? 'biolith:' + BIOLITH_TEXTURES[m[1]] : null;
}

// THE BUILDING KUBES (building-kubes.mjs, the owner's building-kubes proposal confirmed Oct 13): ONE canvas per STYLE
// (texture-building.js), shared by every colour of an idea and by its bits, tinted by each kube's own hex: slot
// 'build:<style>'. Matched FIRST, before the bioliths and every needle below, so "Steel Grate" is never Steel's lattice,
// "Nova Buffed Mineral" never the Mineral crust and "Corrugated Aluminum" never Aluminum's. A pigment idea's name is one
// word (its pigment) and a suffix; the others are named whole. Written out here because this module imports nothing;
// tests/building-kubes.test.mjs holds the table to building-kubes.mjs, name for name.
export const BUILDING_SUFFIXES = Object.freeze([
    [' Chiselled Bricks', 'chisel'], [' Mineral Pillar', 'pillar'], [' Buffed Mineral', 'buffed'], [' Mineral Tiles', 'tiles'],
    [' Dressed Mineral', 'dressed'], [' Rammed Regolith', 'rammed'], [' Sintered Regolith', 'sintered'], [' Regolith Concrete', 'concrete'],
    [' Wood Panels', 'panels'], [' Wood Slats', 'slats'], [' Hazard Stripes', 'hazard'], [' Snow Blocks', 'snow'],
    [' Glow Strip Panel', 'glowstrip'], [' Cut Crystal', 'crystal'], [' Polished Tektite', 'tektite'],
]);
export const BUILDING_EXACT = Object.freeze({
    'Tread Plate': 'tread', 'Steel Grate': 'grate', 'Corrugated Aluminum': 'corrugated', 'Vent Grille': 'vent', 'Ice Bricks': 'icebrick',
    'Lantern Kube': 'lantern', 'Neon Lamp': 'neonlamp', 'Balloon Lamp': 'balloonlamp', 'Hull Plating': 'hull', 'Honeycomb Panel': 'honeycomb', 'Cor Inlaid Panel': 'corinlay',
    'Shellstone': 'shellstone', 'Scale Tiles': 'scaletiles',   // from animals (the owner's Alien Animals items, Oct 29)
});
// The styles whose face is ONE MOTIF (a panel, a lamp, a cut, a column), which an icon shows whole rather than zoomed.
export const BUILDING_WHOLE_FACE = new Set(['vent', 'lantern', 'neonlamp', 'balloonlamp', 'corinlay', 'crystal', 'pillar']);
export function buildingTextureKey(name) {
    const nm = bitBaseName(name || '') || name || '';
    if (Object.prototype.hasOwnProperty.call(BUILDING_EXACT, nm)) return 'build:' + BUILDING_EXACT[nm];
    for (const [suf, style] of BUILDING_SUFFIXES) if (nm.endsWith(suf) && /^\S+$/.test(nm.slice(0, -suf.length))) return 'build:' + style;
    return null;
}

export function kubeTextureType(name, metalness = 0.1) {
    // Resolve a bit to the kube it is an eighth of FIRST, so every rule below sees the base name. Everything after
    // this line is the original chain, unchanged, and a non-bit name is untouched by it.
    const nm = bitBaseName(name || '') || name || '';
    if (buildingTextureKey(nm)) return 'building';
    if (biolithTextureKey(nm)) return 'biolith';
    if (timberTextureKey(nm)) return 'timber';
    if (craftedTextureKey(nm)) return 'crafted';
    if (nativeTextureKey(nm)) return 'native';   // glass panes, ores, ices, manufactured goods, gases and liquids
    if (lifeFamilyOf && lifeFamilyOf(nm)) return 'life';
    if (nm.endsWith('Mineral')) return 'mineral';
    if (nm.endsWith('Regolith')) return 'regolith';
    // The whole name, not a word of it: "Nova Sulfur Crust" is life's (organics.mjs), flat in its own colour, not the Sulfur kube.
    if (metalness > 0.7 || /^(?:Pyrite|Magnetite|Phosphorus|Sulfur)$/.test(nm)) return 'metal';
    return null;
}

// ── WHICH BAKED CANVAS: the cache slot ──────────────────────────────────────────────────────────────────────────
// kubeTextureType answers "which KIND of texture"; applyMaterial then has to pick one baked canvas out of its cache,
// and that second step used to live inside applyMaterial, where nothing without THREE could see it. So nothing could
// say which kubes wear another material's pattern: when this moved here, eleven metals (Aluminum, Titanium and Steel
// among them) fell through to the iron lattice, and that showed only as rusty blocks in the world. It lives here now,
// THREE-free, and applyMaterial calls it, so the catalogue page (doc/catalogue.html) and its test ask exactly the
// question the renderer asks.
//
// The metals, as [needle, slot], in the order they are tried: the first needle the lowercased name CONTAINS wins, which
// is what puts "Iron Bit" in Iron's slot. The first eight are the rectangle lattices TextureEngine has always drawn; the
// other eleven each have a surface of their own in texture-metals.js, and until they did they all fell through to
// Iron's slot and wore its rust. A needle that is a substring of a later one would shadow it, so a longer needle goes
// first; tests/material-looks.test.mjs checks that none does and that every needle claims a real metal.
export const METAL_TEXTURES = [
    ['iron', 'iron'], ['gold', 'gold'], ['copper', 'copper'], ['silver', 'silver'],
    ['pyrite', 'pyrite'], ['magnetite', 'magnetite'], ['phosphorus', 'phosphorus'], ['sulfur', 'sulfur'],
    ['aluminum', 'aluminum'], ['calcium', 'calcium'], ['sodium', 'sodium'], ['manganese', 'manganese'],
    ['titanium', 'titanium'], ['chromium', 'chromium'], ['magnesium', 'magnesium'], ['cobalt', 'cobalt'],
    ['zirconium', 'zirconium'], ['steel', 'steel'], ['structural alloy', 'structuralalloy'],
];
// A metal no needle names still gets a texture rather than none: Iron's lattice, flagged as a fallback everywhere the
// catalogue shows it. No metal in today's catalogue reaches it (tests/catalogue.test.mjs fails if one does); it is here
// for the next one to be added, so that it arrives looking like metal instead of flat.
export const METAL_FALLBACK = 'iron';

// The slot applyMaterial bakes `name` into, given the `type` kubeTextureType chose for it, and whether a rule NAMED
// this kube or it fell through to a default: a metal no needle names wears Iron's, and a 'crafted' type no needle
// claims wears the Mineral crust. A fallback shares its slot with the material that owns it, and a slot that bakes a
// hex in (the rectangle lattices do) bakes the hex of whichever kube reaches it first, so a fallback can also change
// how its owner looks. That is why no current material may be one.
export function textureRoute(type, name) {
    if (type === 'building') { const k = buildingTextureKey(name); return { slot: k || 'mineral', fallback: !k }; }
    if (type === 'biolith') { const k = biolithTextureKey(name); return { slot: k || 'mineral', fallback: !k }; }
    if (type === 'timber') { const k = timberTextureKey(name); return { slot: k || 'mineral', fallback: !k }; }
    if (type === 'life') { const f = lifeFamilyOf && lifeFamilyOf(bitBaseName(name || '') || name); return { slot: f ? 'life:' + f : 'mineral', fallback: !f }; }
    let slot = type, fallback = false;
    if (type === 'metal' && name) {
        const nameLower = name.toLowerCase();
        const hit = METAL_TEXTURES.find(([needle]) => nameLower.includes(needle));
        slot = hit ? hit[1] : null;
        if (!slot) { slot = METAL_FALLBACK; fallback = true; }
    }

    // Crafted kubes key off the NAME rather than `type`, and each name gets its own cache slot (as the ores do) so a
    // texture is always baked from that kube's own hex, never from whichever sibling happened to render first. Applied
    // after the ore chain because it overrides it: "Sulfuric Acid" trips a /Sulfur/ test and can arrive as a metal,
    // but it is a liquid and wants the flow texture. Callers routing through kubeTextureType() arrive with type
    // 'crafted' and land in these same slots; callers that still route ores themselves (the concept + doc pages) send
    // 'metal' plus the name and are overridden here exactly as before.
    // A NATIVE material (a glass pane, an ore, an ice, a manufactured good, a gas or a liquid) is keyed by exact name
    // exactly as the crafted ones are, and for the same reason: what it looks like is a fact about the substance, not
    // about how metallic the recipe left it. Resolved on the BIT-STRIPPED name so "Silica Bit" lands in Silica's slot
    // rather than falling through — kubeTextureType already strips it, and the two must agree or a bit gets the type
    // of its base and the cache slot of nothing.
    const native = nativeTextureKey(bitBaseName(name || '') || name);
    if (native) { slot = native; fallback = false; }

    const crafted = craftedTextureKey(name);
    if (crafted) { slot = crafted; fallback = false; }
    else if (slot === 'crafted') { slot = 'mineral'; fallback = true; }   // 'crafted' with a name no rule claims: fall back to the Mineral crust rather than caching a blank canvas under a junk key
    return { slot, fallback };
}
export function textureSlot(type, name) { return textureRoute(type, name).slot; }
